import React, { createContext, useState, useEffect, useMemo } from 'react';
import { weatherApi } from '../services/api';
import { useStableCallback } from '../hooks/useStableCallback';

// Create context
export const ThemeContext = createContext();

// Weather/season theme constants (static, defined once at module scope)
const WEATHER_THEMES = ['clear', 'clouds', 'rain', 'snow', 'thunderstorm'];
const SEASON_THEMES = ['spring', 'summer', 'autumn', 'winter'];

export const ThemeProvider = ({ children }) => {
  // Theme modes
  const THEME_LIGHT = 'light';
  const THEME_DARK = 'dark';
  const THEME_AUTO = 'auto';

  // Get stored theme from localStorage or default to 'dark'
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || THEME_DARK;
  };

  const [theme, setTheme] = useState(getInitialTheme);
  const [effectiveTheme, setEffectiveTheme] = useState(theme === THEME_AUTO ? determineAutoTheme() : theme);
  // Weather-based theming state
  const [weatherThemeEnabled, setWeatherThemeEnabled] = useState(localStorage.getItem('weatherThemeEnabled') === 'true');
  const [weatherThemeMode, setWeatherThemeMode] = useState(localStorage.getItem('weatherThemeMode') || 'auto');
  const [selectedWeatherTheme, setSelectedWeatherTheme] = useState(localStorage.getItem('selectedWeatherTheme') || 'spring');
  const [weatherCondition, setWeatherCondition] = useState(localStorage.getItem('weatherCondition') || 'spring');
  
  // Determine theme based on time of day
  function determineAutoTheme() {
    const now = new Date();
    const hour = now.getHours();

    // Simple approach: 7:00 AM to 7:00 PM is light mode
    if (hour >= 7 && hour < 19) {
      return THEME_LIGHT;
    } else {
      return THEME_DARK;
    }
  }

  // Milliseconds until the next auto-theme change (7am or 7pm)
  function getMsUntilNextThemeTransition() {
    const now = new Date();
    const nextSevenAm = new Date(now);
    nextSevenAm.setHours(7, 0, 0, 0);
    const nextSevenPm = new Date(now);
    nextSevenPm.setHours(19, 0, 0, 0);

    let nextTransition;
    if (now < nextSevenAm) {
      nextTransition = nextSevenAm;
    } else if (now < nextSevenPm) {
      nextTransition = nextSevenPm;
    } else {
      // After 7pm we wait until tomorrow at 7am
      nextSevenAm.setDate(nextSevenAm.getDate() + 1);
      nextTransition = nextSevenAm;
    }

    return nextTransition.getTime() - now.getTime();
  }

  

  // Update effectiveTheme when theme changes
  useEffect(() => {
    if (theme === THEME_AUTO) {
      setEffectiveTheme(determineAutoTheme());

      // Schedule theme switches precisely at 7am/7pm
      let timeoutId;
      const scheduleNextTransition = () => {
        const delay = getMsUntilNextThemeTransition();
        timeoutId = setTimeout(() => {
          setEffectiveTheme(determineAutoTheme());
          scheduleNextTransition();
        }, delay);
      };

      scheduleNextTransition();

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    } else {
      setEffectiveTheme(theme);
    }
  }, [theme]);

  // Allow other parts of the app to force a recalculation (e.g., after login)
  useEffect(() => {
    if (theme !== THEME_AUTO) {
      return undefined;
    }

    const handleThemeCheck = () => {
      setEffectiveTheme(determineAutoTheme());
    };

    window.addEventListener('focus', handleThemeCheck);
    window.addEventListener('theme:force-update', handleThemeCheck);

    return () => {
      window.removeEventListener('focus', handleThemeCheck);
      window.removeEventListener('theme:force-update', handleThemeCheck);
    };
  }, [theme]);

  // Update localStorage and data-theme attribute when effectiveTheme changes
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', effectiveTheme);
  }, [theme, effectiveTheme]);

  // Helper: map OpenWeather condition to our keys
  const normalizeCondition = (condition) => {
    if (!condition) return 'spring';
    const c = String(condition).toLowerCase();
    if (c.includes('thunder')) return 'thunderstorm';
    if (c.includes('drizzle') || c.includes('rain')) return 'rain';
    if (c.includes('snow')) return 'snow';
    if (c.includes('cloud')) return 'clouds';
    if (['spring', 'summer', 'autumn', 'fall', 'winter'].includes(c)) {
      return c === 'fall' ? 'autumn' : c;
    }
    return 'spring';
  };

  // Helper: compute weather CSS variable overrides based on condition and theme
  const getWeatherVariables = (condition, isDark) => {
    switch (condition) {
      case 'clear':
        return isDark
          ? { '--bg-primary': '#1a1a0a', '--bg-secondary': '#2a2a0a', '--card-bg': '#1a1a0a', '--border-color': 'rgba(255,255,255,0.15)' }
          : { '--bg-primary': '#fff8dc', '--bg-secondary': '#ffeaa7', '--card-bg': '#fff8dc', '--border-color': 'rgba(0,0,0,0.15)' };
      case 'clouds':
        return isDark
          ? { '--bg-primary': '#1a1a1a', '--bg-secondary': '#2a2a2a', '--card-bg': '#1a1a1a', '--border-color': 'rgba(255,255,255,0.15)' }
          : { '--bg-primary': '#e8e8e8', '--bg-secondary': '#d0d0d0', '--card-bg': '#e8e8e8', '--border-color': 'rgba(0,0,0,0.15)' };
      case 'rain':
        return isDark
          ? { '--bg-primary': '#0a1a2a', '--bg-secondary': '#0f2a3a', '--card-bg': '#0a1a2a', '--border-color': 'rgba(255,255,255,0.15)' }
          : { '--bg-primary': '#e0f2f1', '--bg-secondary': '#b2dfdb', '--card-bg': '#e0f2f1', '--border-color': 'rgba(0,0,0,0.15)' };
      case 'snow':
        return isDark
          ? { '--bg-primary': '#1a1a2a', '--bg-secondary': '#2a2a3a', '--card-bg': '#1a1a2a', '--border-color': 'rgba(255,255,255,0.15)' }
          : { '--bg-primary': '#f0f8ff', '--bg-secondary': '#e6f3ff', '--card-bg': '#f0f8ff', '--border-color': 'rgba(0,0,0,0.15)' };
      case 'thunderstorm':
        return isDark
          ? { '--bg-primary': '#0a0a1a', '--bg-secondary': '#1a1a2a', '--card-bg': '#0a0a1a', '--border-color': 'rgba(255,255,255,0.15)' }
          : { '--bg-primary': '#f3e5f5', '--bg-secondary': '#e1bee7', '--card-bg': '#f3e5f5', '--border-color': 'rgba(0,0,0,0.15)' };
      case 'spring':
        return isDark
          ? { '--bg-primary': '#0a2a0a', '--bg-secondary': '#0f3d0f', '--card-bg': '#0d2e0d', '--border-color': 'rgba(255,255,255,0.15)' }
          : { '--bg-primary': '#e8f5e8', '--bg-secondary': '#d4ebd4', '--card-bg': '#f0f9f0', '--border-color': 'rgba(0,0,0,0.15)' };
      case 'summer':
        return isDark
          ? { '--bg-primary': '#0a2a2a', '--bg-secondary': '#0f3d3d', '--card-bg': '#0d2e2e', '--border-color': 'rgba(255,255,255,0.15)' }
          : { '--bg-primary': '#e0f7fa', '--bg-secondary': '#b2ebf2', '--card-bg': '#e0f7fa', '--border-color': 'rgba(0,0,0,0.15)' };
      case 'autumn':
        return isDark
          ? { '--bg-primary': '#2a1a0a', '--bg-secondary': '#3d260f', '--card-bg': '#2e1d0d', '--border-color': 'rgba(255,255,255,0.15)' }
          : { '--bg-primary': '#fbe9e7', '--bg-secondary': '#ffccbc', '--card-bg': '#fbe9e7', '--border-color': 'rgba(0,0,0,0.15)' };
      case 'winter':
        return isDark
          ? { '--bg-primary': '#0a0a2a', '--bg-secondary': '#0f0f3d', '--card-bg': '#0d0d2e', '--border-color': 'rgba(255,255,255,0.15)' }
          : { '--bg-primary': '#e8eaf6', '--bg-secondary': '#c5cae9', '--card-bg': '#e8eaf6', '--border-color': 'rgba(0,0,0,0.15)' };
      default:
        return {};
    }
  };

  const applyWeatherVariables = (vars) => {
    const root = document.documentElement;
    Object.entries(vars || {}).forEach(([k, v]) => root.style.setProperty(k, v));
  };

  // Weather auto fetch when enabled + auto mode
  useEffect(() => {
    const shouldFetch = weatherThemeEnabled && weatherThemeMode === 'auto';
    if (!shouldFetch) return;

    const lastFetch = localStorage.getItem('lastWeatherFetch');
    const twoHoursMs = 2 * 60 * 60 * 1000;
    const now = Date.now();
    if (lastFetch && now - new Date(lastFetch).getTime() < twoHoursMs && localStorage.getItem('weatherCondition')) {
      setWeatherCondition(localStorage.getItem('weatherCondition'));
      return;
    }

    const city = localStorage.getItem('weatherCity') || 'New York';
    const units = localStorage.getItem('weatherUnit') || 'imperial';
    weatherApi.getCurrent(city, units)
      .then((resp) => {
        const c = normalizeCondition(resp?.data?.condition);
        setWeatherCondition(c);
        localStorage.setItem('weatherCondition', c);
        localStorage.setItem('lastWeatherFetch', new Date().toISOString());
        window.dispatchEvent(new StorageEvent('storage', { key: 'weatherCondition', newValue: c, storageArea: localStorage }));
      })
      .catch(() => {});
  }, [weatherThemeEnabled, weatherThemeMode]);

  // Daily theme cycling when cycle modes active
  useEffect(() => {
    if (!weatherThemeEnabled) return;
    if (!['cycle_weather', 'cycle_seasons', 'cycle_all'].includes(weatherThemeMode)) return;

    const weatherThemes = WEATHER_THEMES;
    const seasonThemes = SEASON_THEMES;
    const allThemes = [...weatherThemes, ...seasonThemes];
    const activeThemes = weatherThemeMode === 'cycle_weather' ? weatherThemes : weatherThemeMode === 'cycle_seasons' ? seasonThemes : allThemes;
    const today = new Date().getDate();
    const themeIndex = (today - 1) % activeThemes.length;
    const todayTheme = activeThemes[themeIndex];
    setSelectedWeatherTheme(todayTheme);
    setWeatherCondition(todayTheme);
    localStorage.setItem('selectedWeatherTheme', todayTheme);
    localStorage.setItem('weatherCondition', todayTheme);

    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
    const timeout = nextMidnight.getTime() - now.getTime();
    const timer = setTimeout(() => { window.location.reload(); }, timeout);
    return () => clearTimeout(timer);
  }, [weatherThemeMode, weatherThemeEnabled]);

  // Apply CSS variables based on effective weather condition
  useEffect(() => {
    const isDark = effectiveTheme === THEME_DARK;
    const effectiveCondition = weatherThemeEnabled
      ? (['manual', 'cycle_weather', 'cycle_seasons', 'cycle_all'].includes(weatherThemeMode) ? selectedWeatherTheme : weatherCondition)
      : null;

    if (effectiveCondition) {
      document.documentElement.setAttribute('data-weather-theme', effectiveCondition);
      applyWeatherVariables(getWeatherVariables(effectiveCondition, isDark));
    } else {
      document.documentElement.removeAttribute('data-weather-theme');
      applyWeatherVariables({});
    }
  }, [effectiveTheme, weatherThemeEnabled, weatherThemeMode, selectedWeatherTheme, weatherCondition]);

  // Weather theme controls
  const toggleWeatherTheme = () => {
    const newVal = !weatherThemeEnabled;
    setWeatherThemeEnabled(newVal);
    localStorage.setItem('weatherThemeEnabled', String(newVal));
    if (newVal && weatherThemeMode === 'auto') {
      localStorage.removeItem('lastWeatherFetch');
    }
  };

  const updateWeatherThemeMode = (newMode) => {
    setWeatherThemeMode(newMode);
    localStorage.setItem('weatherThemeMode', newMode);
    if (newMode === 'manual' && weatherThemeEnabled) {
      localStorage.setItem('weatherCondition', selectedWeatherTheme);
      setWeatherCondition(selectedWeatherTheme);
    } else if (newMode === 'auto' && weatherThemeEnabled) {
      localStorage.removeItem('weatherCondition');
      localStorage.removeItem('lastWeatherFetch');
    } else if (['cycle_weather', 'cycle_seasons', 'cycle_all'].includes(newMode) && weatherThemeEnabled) {
      const weatherThemes = WEATHER_THEMES;
      const seasonThemes = SEASON_THEMES;
      const allThemes = [...weatherThemes, ...seasonThemes];
      const activeThemes = newMode === 'cycle_weather' ? weatherThemes : newMode === 'cycle_seasons' ? seasonThemes : allThemes;
      const today = new Date().getDate();
      const themeIndex = (today - 1) % activeThemes.length;
      const todayTheme = activeThemes[themeIndex];
      setSelectedWeatherTheme(todayTheme);
      setWeatherCondition(todayTheme);
      localStorage.setItem('selectedWeatherTheme', todayTheme);
      localStorage.setItem('weatherCondition', todayTheme);
    }
    window.dispatchEvent(new StorageEvent('storage', { key: 'weatherThemeMode', newValue: newMode, storageArea: localStorage }));
  };

  const updateSelectedWeatherTheme = (newTheme) => {
    setSelectedWeatherTheme(newTheme);
    localStorage.setItem('selectedWeatherTheme', newTheme);
    if (weatherThemeMode === 'manual' && weatherThemeEnabled) {
      setWeatherCondition(newTheme);
      localStorage.setItem('weatherCondition', newTheme);
    }
    window.dispatchEvent(new StorageEvent('storage', { key: 'selectedWeatherTheme', newValue: newTheme, storageArea: localStorage }));
  };

  // Stable references so the memoized context value below doesn't get
  // recomputed (and re-render all 35 consumers) just because these plain
  // functions were redefined on a render that didn't actually change any
  // of the state they close over.
  const stableToggleWeatherTheme = useStableCallback(toggleWeatherTheme);
  const stableUpdateWeatherThemeMode = useStableCallback(updateWeatherThemeMode);
  const stableUpdateSelectedWeatherTheme = useStableCallback(updateSelectedWeatherTheme);

  const contextValue = useMemo(() => ({
    theme,
    effectiveTheme,
    changeTheme: setTheme,
    THEME_LIGHT,
    THEME_DARK,
    THEME_AUTO,
    weatherThemeEnabled,
    toggleWeatherTheme: stableToggleWeatherTheme,
    weatherThemeMode,
    updateWeatherThemeMode: stableUpdateWeatherThemeMode,
    selectedWeatherTheme,
    updateSelectedWeatherTheme: stableUpdateSelectedWeatherTheme,
    weatherCondition
  }), [
    theme, effectiveTheme, setTheme, THEME_LIGHT, THEME_DARK, THEME_AUTO,
    weatherThemeEnabled, stableToggleWeatherTheme, weatherThemeMode,
    stableUpdateWeatherThemeMode, selectedWeatherTheme,
    stableUpdateSelectedWeatherTheme, weatherCondition
  ]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider; 
